import React from "react"
import { Box, Progress, Typography } from "@carrotfertility/carotene-core"
import { FormAlert, useFormContext, useWatch } from "@carrotfertility/carotene-core-x"
import { CarrotPlanStep } from "./CarrotPlanStep"
import { CarrotPlanFormOption, MenuItemOptionFields, TopicOptionFields } from "./hooks/useCarrotPlanMappings"
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { DndContext, DragEndEvent, useSensor, useSensors } from "@dnd-kit/core"
import { CSS } from "@dnd-kit/utilities"
import { CarrotPlanFormStep, FieldName, UnslottedStep } from "./CarrotPlanFormTypes"
import { NONE_VALUE } from "./JourneySelection"
import { CustomMouseSensor } from "../../utils/DragNDropCustomSensors"
import { RequiredItemChangeConfirmationModal } from "./RequiredItemChangeConfirmationModal"
import { RequiredMenuItemDetailsModal } from "./RequiredMenuItemDetailsModal"
import { RequiredItemModalsProvider } from "./RequiredItemModalsContext"

type CarrotPlanSteps = {
  employeeName: string
  inReview?: boolean
  stepOptions: CarrotPlanFormOption<MenuItemOptionFields>[]
  topicOptions: CarrotPlanFormOption<TopicOptionFields>[]
  enableRecommendations?: boolean
  recommendedSteps?: UnslottedStep[] | null
  recommendationsErrorStatus?: number
}

export function CarrotPlanSteps({
  employeeName,
  inReview = false,
  stepOptions,
  topicOptions,
  enableRecommendations = false,
  recommendedSteps = null,
  recommendationsErrorStatus
}: CarrotPlanSteps): JSX.Element {
  const steps: CarrotPlanFormStep[] = useWatch({ name: FieldName.steps })
  const primaryJourneyPhase = useWatch({ name: FieldName.primaryJourneyPhase })
  const isPhaseSelected = primaryJourneyPhase !== NONE_VALUE

  const showSelectPhasePrompt = enableRecommendations && !isPhaseSelected
  const isLoadingRecommendations = enableRecommendations && isPhaseSelected && recommendedSteps == null
  const showRecommendationsErrorAlert =
    enableRecommendations && recommendationsErrorStatus != null && recommendationsErrorStatus >= 500
  const { setValue } = useFormContext()
  const sensors = useSensors(useSensor(CustomMouseSensor))

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!active || !over) {
      return
    }

    // Re-order the steps
    if (active.id !== over.id) {
      const activeStepIndex = steps.findIndex((item) => item.id === active.id)
      const overStepIndex = steps.findIndex((item) => item.id === over.id)
      const reorderedSteps = arrayMove(steps, activeStepIndex, overStepIndex)
      updateStepFieldValues(reorderedSteps, setValue)
    }
  }

  const renderBody = () => {
    if (showSelectPhasePrompt) {
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          paddingY={(theme) => theme.spacing(theme.tokens.spacing.xl)}
        >
          <Typography variant="body2" color="text.secondary">
            Select primary journey phase to see recommendations
          </Typography>
        </Box>
      )
    }

    if (isLoadingRecommendations) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={(theme) => theme.spacing(theme.tokens.spacing.sm)}
          paddingY={(theme) => theme.spacing(theme.tokens.spacing.xl)}
        >
          <Progress />
          <Typography variant="body2" color="text.secondary">
            Loading new recommendations…
          </Typography>
        </Box>
      )
    }

    return (
      <Box display="flex" gap={(theme) => theme.spacing(theme.tokens.spacing.lg)} flexDirection="column">
        {/* Modals must remain outside the DndContext as it intercepts pointer events from
        buttons rendered within its subtree, even via portals. */}
        <RequiredItemModalsProvider>
          <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
            <SortableContext items={steps.map((item) => item.id)} strategy={verticalListSortingStrategy}>
              {steps.map((item) => {
                return (
                  <SortableItem key={`${item.id}-${inReview}-sort-item`} id={item.id}>
                    <CarrotPlanStep
                      key={`${item.id}-${inReview}-cp-step`}
                      stepIndex={item.stepIndex}
                      inReview={inReview}
                      options={stepOptions}
                      topicOptions={topicOptions}
                    />
                  </SortableItem>
                )
              })}
            </SortableContext>
          </DndContext>
          <RequiredItemChangeConfirmationModal />
          <RequiredMenuItemDetailsModal />
        </RequiredItemModalsProvider>
      </Box>
    )
  }

  return (
    <Box
      paddingX={(theme) => theme.spacing(theme.tokens.spacing.lg)}
      paddingY={(theme) => theme.spacing(theme.tokens.spacing.xl)}
      border={(theme) => `${theme.tokens.borderWidth.sm} solid ${theme.palette.border.light}`}
      borderRadius={(theme) => theme.tokens.borderRadius.md}
    >
      <Typography
        paddingBottom={(theme) => theme.spacing(theme.tokens.spacing.md)}
        variant="h4"
      >{`${employeeName}'s Carrot Plan`}</Typography>
      {showRecommendationsErrorAlert && (
        <Box paddingBottom={(theme) => theme.spacing(theme.tokens.spacing.md)}>
          <FormAlert severity="warning">
            <Typography variant="body2">
              Recommendations couldn&apos;t be loaded. Please add items manually or try again later.
            </Typography>
          </FormAlert>
        </Box>
      )}
      {renderBody()}
    </Box>
  )
}

const DropIndicator = () => {
  return (
    <Box
      sx={{
        bottom: 0,
        left: 0,
        right: 0,
        height: "3px",
        background: "orange"
      }}
    />
  )
}

function SortableItem({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({ id })

  const style = {
    transform: CSS.Translate.toString(transform),
    cursor: "default"
  }

  return (
    <Box ref={setNodeRef} sx={style} {...attributes} {...listeners}>
      {children}
      {isDragging && <DropIndicator />}
    </Box>
  )
}

const createInputNameWithPrefix = (stepIndex: number, fieldName: string) =>
  `${FieldName.steps}.${stepIndex}.${fieldName}`

function updateStepFieldValues(steps: CarrotPlanFormStep[], setValue: (name: string, value: any) => any) {
  steps.forEach((step, index) => {
    setValue(createInputNameWithPrefix(index, FieldName.menuItem), step.menuItem ?? null)
    setValue(createInputNameWithPrefix(index, FieldName.content), step.content ?? null)
    setValue(createInputNameWithPrefix(index, FieldName.topic), step.topic ?? null)
    setValue(createInputNameWithPrefix(index, FieldName.url), step.url ?? null)
  })
}
